import type { ConciseOperation } from '@festina/collcalc-general-types/collcalc'
import type {
  ConciseBranch,
  ConciseWorkspace,
} from '@festina/configuration-general-types/configuration'
import { notEmpty, type Optional } from '@festina-life/core'
import type {
  QueryClient,
  QueryKey,
  UseMutationOptions,
} from '@tanstack/react-query'

import {
  fetchConciseBranchKey,
  fetchConciseBranchKeyPrefix,
  fetchConciseWorkspaceKey,
  fetchConciseWorkspaceKeyPrefix,
} from '../../keys/configuration'
import type { BranchKey } from '../types'

type ConciseApprovalData = Pick<
  ConciseOperation,
  'approverUserId' | 'approvalTime'
>

const mapChangesetsWithApprovalData = ({
  changesets,
  changesetKey,
  operationKeys,
  approvalData,
}: {
  changesets: ConciseBranch['currentChangesets']
  changesetKey: string
  operationKeys: string[]
  approvalData: ConciseApprovalData
}) =>
  changesets?.map((changeset) =>
    changeset?.changesetKey === changesetKey
      ? {
          ...changeset,
          operations: changeset?.operations?.map((operation) =>
            operation &&
            operation.operationKey &&
            operationKeys.includes(operation.operationKey)
              ? ({
                  ...operation,
                  ...approvalData,
                } satisfies ConciseOperation)
              : operation,
          ),
        }
      : changeset,
  )

export const updateConciseBranchQueryWithApprovalData = ({
  key,
  client,
  changesetKey,
  operationKeys,
  approvalData,
}: {
  key: QueryKey
  client: QueryClient
  changesetKey: string
  operationKeys: string[]
  approvalData: ConciseApprovalData
}) => {
  const conciseBranch = client.getQueryData<Optional<ConciseBranch>>(key)

  if (conciseBranch) {
    client.setQueryData<ConciseBranch>(key, {
      ...conciseBranch,
      currentChangesets: mapChangesetsWithApprovalData({
        changesets: conciseBranch.currentChangesets,
        changesetKey,
        operationKeys,
        approvalData,
      }),
    } satisfies ConciseBranch)
  }

  return conciseBranch
}

export const updateConciseWorkspaceBranchQueryWithApprovalData = ({
  key,
  client,
  changesetKey,
  operationKeys,
  approvalData,
}: {
  key: QueryKey
  client: QueryClient
  changesetKey: string
  operationKeys: string[]
  approvalData: ConciseApprovalData
}) => {
  const conciseWorkspace = client.getQueryData<Optional<ConciseWorkspace>>(key)

  if (conciseWorkspace) {
    client.setQueryData<ConciseWorkspace>(key, {
      ...conciseWorkspace,
      baseChangesets: mapChangesetsWithApprovalData({
        changesets: conciseWorkspace.baseChangesets,
        changesetKey,
        operationKeys,
        approvalData,
      }),
    } satisfies ConciseWorkspace)
  }

  return conciseWorkspace
}

export type ApprovalInputArguments = {
  branchKey: BranchKey
  workspaceNames: string[]
  changesetKey: string
  operationKeys: string[]
}

type OnMutateResult = {
  previousWorkspacesWithBase: ConciseWorkspace[]
  previousWorkspacesExcludingBase: ConciseWorkspace[]
  previousBranchWithBase: Optional<ConciseBranch>
  previousBranchExcludingBase: Optional<ConciseBranch>
}

export const optimisticApprovalConfiguration = ({
  optimisticUserId,
  isApproval,
  queryClient,
}: {
  optimisticUserId?: Optional<string>
  isApproval: boolean
  queryClient: QueryClient
}): UseMutationOptions<
  void,
  Error,
  ApprovalInputArguments,
  OnMutateResult
> => ({
  onMutate: (variables, context) => {
    // Optimistic update to give an instant feedback to the user
    void context.client.cancelQueries({
      queryKey: fetchConciseBranchKeyPrefix({
        server: variables.branchKey.server,
        branchName: variables.branchKey.name,
      }),
    })

    variables.workspaceNames.forEach((workspaceName) => {
      void context.client.cancelQueries({
        queryKey: fetchConciseWorkspaceKeyPrefix({
          server: variables.branchKey.server,
          workspaceName,
        }),
      })
    })

    const updateProps = {
      client: queryClient,
      changesetKey: variables.changesetKey,
      operationKeys: variables.operationKeys,
      approvalData: isApproval
        ? {
            approverUserId: optimisticUserId,
            approvalTime: new Date().toISOString(),
          }
        : { approverUserId: null, approvalTime: null },
    }

    const previousBranchWithBase = updateConciseBranchQueryWithApprovalData({
      key: fetchConciseBranchKey({
        server: variables.branchKey.server,
        branchName: variables.branchKey.name,
        includeBaseChangesets: true,
      }),
      ...updateProps,
    })

    const previousWorkspacesWithBase = variables.workspaceNames
      .map((workspaceName) =>
        updateConciseWorkspaceBranchQueryWithApprovalData({
          key: fetchConciseWorkspaceKey({
            server: variables.branchKey.server,
            workspaceName,
            includeBaseChangesets: true,
          }),
          ...updateProps,
        }),
      )
      .filter(notEmpty)

    const previousBranchExcludingBase =
      updateConciseBranchQueryWithApprovalData({
        key: fetchConciseBranchKey({
          server: variables.branchKey.server,
          branchName: variables.branchKey.name,
          includeBaseChangesets: false,
        }),
        ...updateProps,
      })

    const previousWorkspacesExcludingBase = variables.workspaceNames
      .map((workspaceName) =>
        updateConciseWorkspaceBranchQueryWithApprovalData({
          key: fetchConciseWorkspaceKey({
            server: variables.branchKey.server,
            workspaceName,
            includeBaseChangesets: false,
          }),
          ...updateProps,
        }),
      )
      .filter(notEmpty)

    return {
      previousWorkspacesWithBase,
      previousWorkspacesExcludingBase,
      previousBranchWithBase,
      previousBranchExcludingBase,
    }
  },
  // If the mutation fails,
  // use the result returned from onMutate to roll back
  // oxlint-disable-next-line max-params
  onError: (_err, variables, onMutateResult, context) => {
    context.client.setQueryData(
      fetchConciseBranchKey({
        server: variables.branchKey.server,
        branchName: variables.branchKey.name,
        includeBaseChangesets: true,
      }),
      onMutateResult?.previousBranchWithBase,
    )

    context.client.setQueryData(
      fetchConciseBranchKey({
        server: variables.branchKey.server,
        branchName: variables.branchKey.name,
        includeBaseChangesets: false,
      }),
      onMutateResult?.previousBranchExcludingBase,
    )

    onMutateResult?.previousWorkspacesWithBase.forEach(
      (previousWorkspaceWithBase) => {
        context.client.setQueryData(
          fetchConciseWorkspaceKey({
            server: variables.branchKey.server,
            workspaceName: previousWorkspaceWithBase.name,
            includeBaseChangesets: true,
          }),
          previousWorkspaceWithBase,
        )
      },
    )

    onMutateResult?.previousWorkspacesExcludingBase.forEach(
      (previousWorkspaceExcludingBase) => {
        context.client.setQueryData(
          fetchConciseWorkspaceKey({
            server: variables.branchKey.server,
            workspaceName: previousWorkspaceExcludingBase.name,
            includeBaseChangesets: false,
          }),
          previousWorkspaceExcludingBase,
        )
      },
    )
  },
  // As specified by Tanstack docs on optimistic updates: Always refetch after error or success:
  // oxlint-disable-next-line max-params
  onSettled: (_data, _error, variables, _onMutateResult, context) => {
    void context.client.invalidateQueries({
      queryKey: fetchConciseBranchKeyPrefix({
        server: variables.branchKey.server,
        branchName: variables.branchKey.name,
      }),
    })

    variables.workspaceNames.forEach((workspaceName) => {
      void context.client.invalidateQueries({
        queryKey: fetchConciseWorkspaceKeyPrefix({
          server: variables.branchKey.server,
          workspaceName,
        }),
      })
    })
  },
})
